import { Organization } from "models/Organization";

export enum DeviceModel {
  Unknown = 0,
  G200 = 1,
}

export enum DeviceKind {
  Unknown,
  Test,
  Production,
}

export type Device = {
  id: number;
  serialNumber: string;
  model: DeviceModel;
  kind: DeviceKind;
  group: number | null;
  connected: boolean;
  organizations: Organization[];
  remoteAccess: boolean;
  createdAt: string;
  updatedAt: string;
};

export enum ProcessState {
  NotInitialized = 10,
  Homing = 20,
  Initializing = 30,
  IdleControledErrorActive = 40,
  IdleReady = 50,
  ManualMode = 60,
  Ventilation = 70,
  NormalMode = 80,
  CalendarIdle = 90,
  Cleaning = 100,
  Ignition = 110,
  Boost = 120,
  Heating = 130,
  Standby = 140,
  Shutdown = 150,
  ControlledShutdown = 160,
  EmergencyShutdown = 170,
  PowerLossShutdown = 180,
  Cooling = 190,
}

export enum WorkMode {
  startInitialization = 20,
  startNormalMode = 60,
  startCalendarMode = 70,
  startVentilationMode = 80,
  stopManualCalendarOrVentilationMode = 90,
}

export type HeaterCommon = {
  processState: ProcessState;
  machineName: string;
  targetRoomTemperature: number;
  softwareVersion: string;
  warningCount: number;
  errorCount: number;
};

export type Heater = HeaterCommon & {
  device: Device;
};

export type Parameters = {
  energyMeter: number;
  targetRoomTemperature: number;
  ashLevel: number;
  timePredictionTillNextMaintenance: number;
  maintenanceInterval: number;
  targetHotAirTemperature: number;
  hotAirFanPower: number;
  processState: number;
  machineName: string;
  operationHoursHeating: number;
  softwareVersion: string;
};

export type ActivityRecordBase = {
  firstName: string;
  lastName: string;
  email: string;
  timestamp: string;
};

export type ActivityRecord = ActivityRecordBase & {
  code: number;
  previousValue: string;
  currentValue: string;
};

export type HeaterErrorOrWarningInfo = {
  id: number;
  source: number;
  code: number;
  timeOn: string;
  timeOff: string;
  timeConfirm: string;
  createdAt: string;
  updatedAt: string;
};

type HeaterActivities = {
  resetAshLevel: ActivityRecordBase;
  resetHoursTillMaintenance: ActivityRecordBase;
  startInitialization: ActivityRecordBase;
  startNormalMode: ActivityRecordBase;
  startCalendarMode: ActivityRecordBase;
  startVentilationMode: ActivityRecordBase;
  stopManualCalendarOrVentilationMode: ActivityRecordBase;
  parameters: ActivityRecord[];
};

export type HeaterWorkModeActivities = Omit<
  HeaterActivities,
  "parameters" | "resetAshLevel" | "resetHoursTillMaintenance"
>;

export type HeaterMaintenanceActivities = Pick<
  HeaterActivities,
  "resetAshLevel" | "resetHoursTillMaintenance"
>;

export type HeaterDashboard = {
  device: Device;
  parameters: Parameters;
  activities: HeaterActivities;
  warnings: HeaterErrorOrWarningInfo[];
  errors: HeaterErrorOrWarningInfo[];
};

export type HeaterDataAttributes = {
  id: number;
  source: number;
  energyMeter: number;
  targetRoomTemperature: number;
  ashLevel: number;
  timePredictionTillNextMaintenance: number;
  maintenanceInterval: number;
  targetHotAirTemperature: number;
  hotAirFanPower: number;
  processState: number;
  machineName: string;
  operationHoursHeating: number;
  softwareVersion: string;
  serialNumber: string;
  remoteAccess: boolean;
};

export type HeaterDataAllAttributes = HeaterDataAttributes & {
  heaterPower: number;
  energyQuantityPellets: number;
  feedingQuantityPellets: number;
  grillInBasePosition: boolean;
  potentiometerdrivegrillCleaningCyclePelletsFeeding: number;
  potentiometerdrivegrillRemainingFeedingTimeTillCleaning: number;
  flueGasOptimalOxygenContent: number;
  releaseCleaningTemperature: number;
  powerCombustionAirFanCleaning: number;
  ashLevelFactor: number;
  ashConveyorWaitTimeHeatingPhase: number;
  ashConveyorRunTimeHeatingPhase: number;
  ignitionWithIgniterTemperature: number;
  differentialOilPressureMaxTemperature: number;
  normalOperationTemperature: number;
  pelletFeederOnTimeIgnition: number;
  pelletFeederOffTimeIgnition: number;
  amountOfFeedingCyclesIgnition: number;
  powerCombustionAirFanIgnition: number;
  flueGasFlapInBasePosition: boolean;
  airFlapInBasePosition: boolean;
  combustionChamberBoostTemperatureLimit: number;
  combustionChamberTemperatureBoostEnd: number;
  timeLimitBoost: number;
  pelletFeederOnTimeBoost: number;
  pelletFeederOffTimeBoost: number;
  hotAirFanPowerVentilation: number;
  modeHotAirFan: boolean;
  positiveHysteresisHeatingRequest: number;
  pelletFeedingMode: number;
  pelletFeederOnTimeHeating: number;
  pelletFeederOffTimeHeating: number;
  pelletFeederMinOnTime: number;
  pelletFeederMinOffTime: number;
  pelletFeederCycleTime: number;
  negativeHysteresisHeatingRequest: number;
  additionalCooling: boolean;
  additionalCoolingDelay: number;
  maxFlueGasInletTemperatureForPelletFeeding: number;
  densityDryAirAt20C: number;
  specificThermalCapacityDryAir: number;
  maximumFlowRateHotAirFan: number;
  hotAirThermalPower: number;
  heaterEfficiency: number;
  combustionChamberShutdownTemperature: number;
  hotAirFanShutdownTemperature: number;
  flueGasInletTemperatureShutdownFinished: number;
  minimumShutdownDuration: number;
  maxOilOutletTemperature: number;
  maxTemperatureInCombustionChamber: number;
  maxTemperatureBelowCombustionChamber: number;
  maxOilInletTemperature: number;
  underpressureDelayErrorMonitoring: number;
  maxFlueGasInletTemperature: number;
  maxFlueGasOutletTemperature: number;
  maxResidualOxygenContent: number;
  upperPowerLimitHotAirFan: number;
  lowerPowerLimitHotAirFan: number;
  hotAirLowerTemperatureLimit: number;
  hotAirTemperatureHysteresis: number;
  hotAirUpperTemperatureLimit: number;
  hotAirTemperatureMaximum: number;
  minOilInletTemperatureStartHotAirFan: number;
  ignitionTimeout: number;
  voltageMonitoring: boolean;
  vegaProbeDistance: number;
  pelletLevel: number;
  pelletLevelMin: number;
  pelletLevelMax: number;
  pelletRefillMonitoringInterval: number;
  hotAirTemperatureInvalidSignal: boolean;
  combustionChamberTemperatureSignalInvalid: boolean;
  combustionChamberBelowTemperatureSignalInvalid: boolean;
  flueGasInletTemperatureInvalidSignal: boolean;
  flueGasOutletTemperatureInvalidSignal: boolean;
  thermalOilInletTemperatureInvalidSignal: boolean;
  thermalOilOutletTemperatureInvalidSignal: boolean;
  roomAirTemperatureInvalidSignal: boolean;
  outsideAirTemperatureInvalidSignal: boolean;
  tertiaryAirTemperatureInvalidSignal: boolean;
  lambdaProbeInvalidSignal: boolean;
  partialLoadMode: boolean;
  powerPreselect: number;
  operationHoursHeating: number;
  operationSecondsHeating: number;
  operationHoursStandby: number;
  operationSecondsStandby: number;
  operationHoursError: number;
  operationSecondsError: number;
  operationHoursWarning: number;
  operationSecondsWarning: number;
  operationHoursVentilation: number;
  operationSecondsVentilation: number;
  operationHoursService: number;
  operationSecondsService: number;
  serviceInterval: number;
  serviceDelay: number;
  timePredictionTillNextService: number;
  operationHoursMaintenance: number;
  operationSecondsMaintenance: number;
  maintenanceInterval: number;
  maintenanceDelay: number;
  timePredictionTillNextMaintenance: number;
  pelletConveyorDriveRunningTime: number;
  ashConveyorDriveRunningTime: number;
  pelletVacuumDriveRunningTime: number;
  oilPumpDriveRunningTime: number;
  hotAirFanDriveRunningTime: number;
  grillDriveRunningTime: number;
  kpHotAirFan: number;
  kiHotAirFan: number;
  kiLimitHotAirFan: number;
  kpPelletFeeding: number;
  kiPelletFeeding: number;
  kiLimitPelletFeeding: number;
  kpCombustionAirFan: number;
  kiCombustionAirFan: number;
  kiLimitCombustionAirFan: number;
  outMaximumCombustionAirFan: number;
  minCombustionAirAt0PelletFeed: number;
  minCombustionAirAt100PelletFeed: number;
  airFlapInvertDirection: boolean;
  airFlapSetPointIgnition: number;
  airFlapSetPointBoost: number;
  airFlapSetPointHeating: number;
  airFlapSetPointStandby: number;
  flueGasFlapInvertDirection: boolean;
  flueGasFlapSetPointIgnition: number;
  flueGasFlapSetPointBoost: number;
  flueGasFlapSetPointHeating: number;
  flueGasFlapSetPointStandby: number;
  flueGasProbeVoltage: number;
  powerSupplyVoltage: number;
  vegaSensorCurrent: number;
  multiPurposeAnalogInCurrent: number;
  multiPurposeAnalogInCurrentScalingMinimum: number;
  multiPurposeAnalogInCurrentScalingMaximum: number;
  multiPurposeAnalogInVoltage: number;
  multiPurposeAnalogInVoltageScalingMinimum: number;
  multiPurposeAnalogInVoltageScalingMaximum: number;
  analogOutputCombustionAirFanPower: number;
  analogOutputHotAirFanPower: number;
  analogOutputThermalOilPumpPower: number;
  resistanceInputOne: number;
  resistanceInputTwo: number;
  resistanceInputThree: number;
  exhaustAir: number;
  outsideAir: number;
  tertiaryAir: number;
  oilInlet: number;
  oilOutlet: number;
  combustionChamberBelow: number;
  roomAir: number;
  unitRoomTemperature: number;
  reserveTemperature1: number;
  reserveTemperature2: number;
  flueGasInlet: number;
  flueGasOutlet: number;
  reserveTemperature3: number;
  reserveTemperature4: number;
  combustionChamber: number;
  roomAirFromPt100: number;
  roomAirTemperatureSource: number;
  door1Closed: boolean;
  door2Closed: boolean;
  underpressureOk: boolean;
  oilLeakageOk: boolean;
  phaseOk: boolean;
  emergencyStopOk: boolean;
  hotAirFanOk: boolean;
  comnbustionAirFanOk: boolean;
  thermalOilPumpOk: boolean;
  ashConveyorOk: boolean;
  pelletsConveyorOk: boolean;
  pelletsVacuumOk: boolean;
  flueGasOk: boolean;
  differentialOilPressureOk: boolean;
  oilInletOk: boolean;
  oilOutletOk: boolean;
  emergencyStopIoboardOk: boolean;
  powerSupply1Ok: boolean;
  powerSupply2Ok: boolean;
  emergencyStopPressed: boolean;
  grillOk: boolean;
  positionSensorGrill: boolean;
  outputIgniter: boolean;
  outputPelletsFeeder: boolean;
  outputAshFeeder: boolean;
  outputCombustionFan: boolean;
  outputHotAirFan: boolean;
  outputPelletsVacuum: boolean;
  outputThermalOilPump: boolean;
  outputGrillRight: boolean;
  outputGrillLeft: boolean;
  outputFlapOneOpen: boolean;
  outputFlapOneClose: boolean;
  outputFlapTwoOpen: boolean;
  outputFlapTwoClose: boolean;
  outputResetEStop: boolean;
  ioboardFirmwareVersion: string;
  ioboardHardwareVersion: number;
  passwordExpireTime: number;
  simpleGrillDrive: boolean;
  airFlapTypePrimaryAir: boolean;
  cpuTemperature: number;
  delayForDisplayDimming: number;
  displayBrightness: number;
  displayBrightnessLatch: number;
  createdAt: Date;
  updatedAt: Date;
};

type AdminDeviceType = Device & {
  organizations: Organization[];
};

export type AdminDevice = {
  device: AdminDeviceType;
  keysAndCerts: { hasMqtt: boolean; hasOvpn: boolean };
  parameters: {
    machineName: string;
  };
};

export type HeaterErrors = {
  data: HeaterErrorOrWarningInfo[];
};
